<script setup>
import { computed, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatBytes } from 'shared/helpers/FileHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import AttachmentPreviewDialog from './AttachmentPreviewDialog.vue';

const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['removeAttachment']);

const { t } = useI18n();

const previewDialog = ref(null);
const previewAttachment = ref(null);

const nonRecordedAudioAttachments = computed(() => {
  return props.attachments.filter(attachment => !attachment?.isVoiceMessage);
});

const recordedAudioAttachments = computed(() =>
  props.attachments.filter(attachment => attachment.isVoiceMessage)
);

const onRemoveAttachment = itemIndex => {
  emit(
    'removeAttachment',
    nonRecordedAudioAttachments.value
      .filter((_, index) => index !== itemIndex)
      .concat(recordedAudioAttachments.value)
  );
};

// Open the preview dialog. previewAttachment must propagate to the dialog as a
// prop before we call open(), so we wait a tick.
const openPreview = async attachment => {
  previewAttachment.value = attachment;
  await nextTick();
  previewDialog.value?.open();
};

const formatFileSize = file => {
  const size = file.byte_size || file.size;
  return formatBytes(size, 0);
};

const isTypeImage = file => {
  const type = file.content_type || file.type;
  return type.includes('image');
};

const fileName = file => {
  return file.filename || file.name;
};
</script>

<template>
  <div class="flex flex-wrap gap-y-1 gap-x-2 overflow-auto max-h-[12.5rem]">
    <div
      v-for="(attachment, index) in nonRecordedAudioAttachments"
      :key="attachment.id"
      class="flex items-center p-1 bg-n-slate-3 gap-1 rounded-md w-[15rem]"
    >
      <button
        type="button"
        class="flex items-center flex-1 min-w-0 gap-1 text-left rounded-sm cursor-pointer hover:bg-n-slate-4"
        :aria-label="t('CONVERSATION.REPLYBOX.ATTACHMENT_PREVIEW.OPEN')"
        @click="openPreview(attachment)"
      >
        <div class="max-w-[4rem] flex-shrink-0 w-6 flex items-center">
          <img
            v-if="isTypeImage(attachment.resource)"
            class="object-cover w-6 h-6 rounded-sm"
            :src="attachment.thumb"
          />
          <span v-else class="relative w-6 h-6 text-lg text-left -top-px">
            📄
          </span>
        </div>
        <div class="max-w-3/5 min-w-[50%] overflow-hidden text-ellipsis">
          <span
            class="h-4 overflow-hidden text-sm font-medium text-ellipsis whitespace-nowrap"
          >
            {{ fileName(attachment.resource) }}
          </span>
        </div>
        <div class="w-[30%] justify-center">
          <span class="overflow-hidden text-xs text-ellipsis whitespace-nowrap">
            {{ formatFileSize(attachment.resource) }}
          </span>
        </div>
      </button>
      <div class="flex items-center justify-center">
        <Button
          ghost
          slate
          xs
          icon="i-lucide-x"
          @click="onRemoveAttachment(index)"
        />
      </div>
    </div>

    <AttachmentPreviewDialog
      ref="previewDialog"
      :attachment="previewAttachment"
    />
  </div>
</template>
